import { ApolloError } from '@apollo/client';
import { useCallback, useState } from 'react';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import fetchAvailableStatesForCountryQuery from './queries/fetch-available-states-for-country';
import {
  FetchAvailableStatesForCountriesQuery,
  FetchAvailableStatesForCountriesQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';

const LOCAL_STORAGE_KEY_PREFIX = 'availableStates';

interface UseCountryStateServiceHook extends CachedStatesResponse {
  fetchStatesForCountry: (isoCountry: string) => void;
}

export interface CachedStatesResponse {
  hasStatesCompleted: boolean;
  isLoadingStates: boolean;
  fetchStatesError?: ApolloError;
  states?: FetchAvailableStatesForCountriesQuery['availableStatesForCountry'];
  taxLabels?: FetchAvailableStatesForCountriesQuery['availableTaxLabels'];
}

const defaultResponse = {
  isLoadingStates: false,
  hasStatesCompleted: false,
  fetchStatesError: undefined,
  states: undefined,
  taxLabels: undefined,
};

export const useCountryStateService = (): UseCountryStateServiceHook => {
  const getCachedValue = useCallback((isoCode) => {
    const cachedValue = window.sessionStorage.getItem(`${LOCAL_STORAGE_KEY_PREFIX}-${isoCode}`);

    return cachedValue ? (JSON.parse(cachedValue) as CachedStatesResponse) : undefined;
  }, []);

  const [response, setResponse] = useState<CachedStatesResponse>();

  const updateResponse = useCallback((countryIsoCode: string, updatedResponse: CachedStatesResponse) => {
    if (countryIsoCode) {
      window.sessionStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}-${countryIsoCode}`, JSON.stringify(updatedResponse));
      setResponse(updatedResponse);
    }
  }, []);

  const gqlClient = graphqlClient();

  const fetchStatesForCountry = useCallback(
    async (isoCode: string) => {
      const cachedValue = getCachedValue(isoCode);
      if (!cachedValue || !cachedValue.states) {
        updateResponse(isoCode, {
          hasStatesCompleted: false,
          isLoadingStates: true,
          fetchStatesError: undefined,
          states: undefined,
          taxLabels: undefined,
        });
        // useLazyQuery and useQuery were sending multiple requests.
        const { data, error } = await gqlClient.query<
          FetchAvailableStatesForCountriesQuery,
          FetchAvailableStatesForCountriesQueryVariables
        >({
          query: fetchAvailableStatesForCountryQuery,
          variables: {
            isoCode,
          },
        });

        updateResponse(isoCode, {
          hasStatesCompleted: true,
          isLoadingStates: false,
          fetchStatesError: error,
          states: data?.availableStatesForCountry,
          taxLabels: data?.availableTaxLabels,
        });
      } else {
        setResponse(cachedValue);
      }
    },
    [getCachedValue, updateResponse]
  );

  return {
    ...defaultResponse,
    ...response,
    fetchStatesForCountry,
  };
};

export default useCountryStateService;
